// Keeps the Supabase library (~100 KB) out of the first page load
// (2026-10-07). Public pages never need it; the admin, account and booking
// pages import ./client themselves, inside their own lazy chunks.
//
// AuthContext sits at the root and must still learn about a session as soon
// as the client exists — whichever page created it — so the client announces
// itself here and AuthContext subscribes through whenSupabaseReady() instead
// of importing the client directly.
import type { supabase as SupabaseClient } from './client';

type Client = typeof SupabaseClient;

let ready: Client | null = null;
const waiting = new Set<(client: Client) => void>();

/** Called once by ./client right after the client is created. */
export function announceSupabase(client: Client) {
  ready = client;
  waiting.forEach((callback) => callback(client));
  waiting.clear();
}

/** Runs `callback` with the client once it exists (now, if it already does).
 *  Returns a function that cancels a callback that has not run yet. */
export function whenSupabaseReady(callback: (client: Client) => void): () => void {
  if (ready) {
    callback(ready);
    return () => {};
  }
  waiting.add(callback);
  return () => {
    waiting.delete(callback);
  };
}

/** Loads (or returns) the client. */
export const loadSupabase = (): Promise<Client> =>
  import('./client').then((module) => module.supabase);
